import React, { useEffect, useRef, useCallback } from 'react';

interface FiberNode {
  type: any;
  elementType: any;
  stateNode: any;
  memoizedProps: any;
  memoizedState: any;
  return: FiberNode | null;
  child: FiberNode | null;
  sibling: FiberNode | null;
  _debugOwner: FiberNode | null;
  _debugSource?: {
    fileName?: string;
    lineNumber?: number;
    columnNumber?: number;
  };
  tag: number;
}

interface ReactComponent {
  displayName?: string;
  name?: string;
  props?: any;
  state?: any;
  type: 'class' | 'function' | 'host';
  selector?: string;
  source?: {
    fileName?: string;
    lineNumber?: number;
    columnNumber?: number;
  };
  // Only added in component tree, not in single component
  renderedBy?: string[];
  renders?: string[];
}

interface ElementInfo {
  selector: string;
  tagName: string;
  id?: string;
  className?: string;
  innerText?: string;
  attributes: Record<string, string>;
  rect: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  styles: {
    display: string;
    position: string;
    zIndex: string;
    color: string;
    backgroundColor: string;
  };
  react?: {
    component: ReactComponent | null;
    tree: ReactComponent[];
  };
}

// React Fiber inspection functions
function findReactFiber(domElement: Element): FiberNode | null {
  const key = Object.keys(domElement).find(
    (k) => k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance'),
  );

  if (!key) {
    return null;
  }

  return (domElement as any)[key];
}

function extractHookState(hookState: any): any {
  const hooks: any[] = [];
  let current = hookState;

  while (current) {
    if (current.memoizedState !== undefined) {
      hooks.push({
        type: getHookType(current),
        value: serializeValue(current.memoizedState),
      });
    }
    current = current.next;
  }

  return hooks.length > 0 ? hooks : null;
}

function getHookType(hook: any): string {
  // This is a simplified hook type detection
  if (hook.queue) {
    return 'useState';
  }
  if (hook.deps) {
    return 'useEffect';
  }
  return 'unknown';
}

function serializeValue(value: any, maxDepth: number = 3, currentDepth: number = 0): any {
  if (currentDepth >= maxDepth) {
    return '[Max depth reached]';
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'function') {
    return '[Function]';
  }
  if (typeof value === 'symbol') {
    return value.toString();
  }

  if (typeof value === 'object') {
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (value instanceof RegExp) {
      return value.toString();
    }
    if (value instanceof Error) {
      return { name: value.name, message: value.message };
    }

    // Handle React elements
    if (value.$$typeof) {
      return '[React Element]';
    }

    // Handle circular references
    if (value.__serialized) {
      return '[Circular Reference]';
    }
    value.__serialized = true;

    try {
      if (Array.isArray(value)) {
        const result = value.map((item) => serializeValue(item, maxDepth, currentDepth + 1));
        delete (value as any).__serialized;
        return result;
      }

      const result: any = {};
      for (const [key, val] of Object.entries(value)) {
        if (key === '__serialized') {
          continue;
        }
        result[key] = serializeValue(val, maxDepth, currentDepth + 1);
      }
      delete value.__serialized;
      return result;
    } catch (_e) {
      delete value.__serialized;
      return '[Serialization Error]';
    }
  }

  return value;
}

function hasSerializableProps(props: any): boolean {
  if (!props || typeof props !== 'object') {
    return false;
  }

  if (Array.isArray(props)) {
    return props.every(hasSerializableProps);
  }

  try {
    JSON.stringify(props);
    return true;
  } catch (_e) {
    return false;
  }
}

const escapeCssIdentifier = (value: string): string => {
  try {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }
  } catch (_err) {
    // Ignore and fallback to manual escaping
  }
  return value.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
};

const getElementClassName = (el: Element): string | undefined => {
  const className = (el as HTMLElement).className;
  if (typeof className === 'string') {
    return className || undefined;
  }

  const svgClass = (el as SVGElement).className;
  if (svgClass && typeof svgClass === 'object' && 'baseVal' in svgClass) {
    return svgClass.baseVal || undefined;
  }

  return undefined;
};

function generateUniqueSelector(el: Element): string {
  if (el.id) {
    return `#${escapeCssIdentifier(el.id)}`;
  }

  const path: string[] = [];
  let current: Element | null = el;

  while (current && current.nodeType === Node.ELEMENT_NODE) {
    let selector = current.tagName.toLowerCase();

    const className = getElementClassName(current);
    if (className) {
      const classes = className
        .trim()
        .split(/\s+/)
        .filter((c) => c && !c.startsWith('__picker'))
        .map(escapeCssIdentifier);
      if (classes.length > 0) {
        selector += '.' + classes.join('.');
      }
    }

    if (current.parentElement) {
      const siblings = Array.from(current.parentElement.children).filter(
        (sibling) => sibling.tagName === current!.tagName,
      );

      if (siblings.length > 1) {
        const index = siblings.indexOf(current) + 1;
        selector += `:nth-child(${index})`;
      }
    }

    path.unshift(selector);

    if (current.tagName.toLowerCase() === 'body') {
      break;
    }

    current = current.parentElement;
  }

  return path[path.length - 1]
}

function getComponentTree(domElement: Element): ReactComponent[] {
  const tree: ReactComponent[] = [];
  const fiber = findReactFiber(domElement);

  if (!fiber) {
    return tree;
  }

  let current: FiberNode | null = fiber;
  while (current) {
    try {
      const props = current.memoizedProps ? { ...current.memoizedProps } : {};

      let componentType: 'class' | 'function' | 'host' = 'host';
      if (typeof current.type === 'string') {
        componentType = 'host';
      } else if (current.type?.prototype?.isReactComponent) {
        componentType = 'class';
      } else if (typeof current.type === 'function') {
        componentType = 'function';
      }

      const component: ReactComponent = {
        displayName: getComponentDisplayName(current),
        name:
          typeof current.type === 'function'
            ? current.type.displayName || current.type.name
            : undefined,
        type: componentType,
        props: serializeValue(props),
      };

      if (componentType === 'host' && current.stateNode instanceof Element) {
        component.selector = generateUniqueSelector(current.stateNode);
      }

      const source =
        current._debugSource ||
        current.memoizedProps?._source ||
        (current._debugOwner && current._debugOwner.memoizedProps?._source);

      if (source) {
        component.source = {
          fileName: source.fileName,
          lineNumber: source.lineNumber,
          columnNumber: source.columnNumber,
        };
      }

      if (componentType === 'function' && current.memoizedState) {
        component.state = extractHookState(current.memoizedState);
      }

      if (componentType === 'class' && current.stateNode?.state) {
        component.state = serializeValue(current.stateNode.state);
      }

      tree.unshift(component);
    } catch (e) {
      console.warn('Error inspecting component:', e);
    }
    current = current.return;
  }

  return tree;
}

function getComponentDisplayName(fiber: FiberNode): string {
  if (typeof fiber.type === 'string') {
    return fiber.type; // host element like 'div', 'span'
  }

  // Handle Context Providers
  if (fiber.type && fiber.type._context) {
    const contextName = fiber.type._context.displayName || fiber.type._context._currentValue2?.displayName || 'Context';
    return `${contextName}.Provider`;
  }

  // Handle Context Consumers
  if (fiber.type && fiber.type.Consumer) {
    const contextName = fiber.type.displayName || 'Context';
    return `${contextName}.Consumer`;
  }

  // Handle React.Fragment
  if (fiber.type && fiber.type === (React as any).Fragment) {
    return 'React.Fragment';
  }

  // Handle forwardRef components
  if (fiber.type && fiber.type.$typeof && fiber.type.render) {
    const wrappedName = fiber.type.render.displayName || fiber.type.render.name || 'Component';
    return `forwardRef(${wrappedName})`;
  }

  // Handle memo components
  if (fiber.type && fiber.type.$typeof && fiber.type.type) {
    const wrappedName = fiber.type.type.displayName || fiber.type.type.name || 'Component';
    return `memo(${wrappedName})`;
  }

  // Standard component names
  if (fiber.type?.displayName) {
    return fiber.type.displayName;
  }

  if (fiber.type?.name) {
    return fiber.type.name;
  }

  // Handle special React elements
  if (fiber.elementType?.displayName) {
    return fiber.elementType.displayName;
  }

  if (fiber.elementType?.name) {
    return fiber.elementType.name;
  }

  // Handle elementType for Context Providers
  if (fiber.elementType && fiber.elementType._context) {
    const contextName = fiber.elementType._context.displayName || 'Context';
    return `${contextName}.Provider`;
  }

  return 'Anonymous';
}

const normalizeComponentName = (name?: string | null): string | null => {
  if (!name) {
    return null;
  }
  const cleaned = name
    .replace(/^memo\((.*)\)$/, '$1')
    .replace(/^forwardRef\((.*)\)$/, '$1')
    .replace(/^with[A-Z][A-Za-z]*\((.*)\)$/, '$1')
    .split('$')[0];
  return cleaned !== 'Anonymous' ? cleaned : null;
};

function getNearestComponentName(el: Element | null): string | null {
  if (!el) {
    return null;
  }

  const originalTree = getComponentTree(el);
  if (originalTree.length === 0) {
    return null;
  }

  const filteredTree = originalTree.filter((comp) => {
    const name = comp.displayName || comp.name || '';
    return !(
      name.includes('Provider') ||
      name.includes('Primitive') ||
      name === 'Presence' ||
      name === 'Portal' ||
      name === 'FocusScope' ||
      name === 'DismissableLayer' ||
      name === 'Anonymous' ||
      name.includes('Boundary') ||
      name.includes('Router') ||
      name === 'main'
    );
  });

  if (filteredTree.length === 0) {
    return null;
  }

  const reactComponents = filteredTree.filter(
    (comp) => comp.type === 'function' || comp.type === 'class',
  );

  let topComponent: ReactComponent | null = null;
  if (reactComponents.length > 0) {
    topComponent = reactComponents[reactComponents.length - 1];
  }

  if (!topComponent) {
    // If no React component found, fall back to last HTML element (like div)
    const lastHtml = filteredTree[filteredTree.length - 1];
    const fallbackName = normalizeComponentName(lastHtml.displayName) || lastHtml.displayName;
    return fallbackName || null;
  }

  return (
    normalizeComponentName(topComponent.displayName) ||
    normalizeComponentName(topComponent.name) ||
    null
  );
}

const PICKER_STYLE_CSS = `
  .__picker-overlay, .__picker-dotted {
    position: fixed;
    z-index: 2147483647;
    pointer-events: none;
    transition: top .03s, left .03s, width .03s, height .03s;
    box-sizing: border-box;
  }
  .__picker-overlay {
    border: 2px solid #4da3ff;
    box-shadow: 0 0 0 99999px rgba(77,163,255,0.12) inset;
    border-radius: 6px;
  }
  .__picker-dotted {
    border: 2px dotted #4da3ff;
    border-radius: 4px;
    display: none;
  }
  .__picker-label {
    position: fixed;
    z-index: 2147483647;
    pointer-events: none;
    font: 11px/1.4 -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
    color: #eaf3ff;
    background: #2979ff;
    border: 1px solid #1e63d6;
    border-radius: 4px;
    padding: 3px 6px;
    max-width: 50vw;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export function ElementPickerProvider({ children }: { children: React.ReactNode }) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const dottedRef = useRef<HTMLDivElement | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const activeRef = useRef(false);
  const targetRef = useRef<Element | null>(null);

  const cleanup = useCallback(() => {
    activeRef.current = false;
    [overlayRef.current, dottedRef.current, labelRef.current, styleRef.current].forEach((el) => {
      if (el) {
        el.remove();
      }
    });
    overlayRef.current = null;
    dottedRef.current = null;
    labelRef.current = null;
    styleRef.current = null;

    (window as any).__elementPicker = { active: false, stop: () => {} };

    // Notify parent that picker stopped
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'ELEMENT_PICKER_STATUS',
          active: false,
          source: 'element-picker',
        },
        '*',
      );
    }
  }, []);

  const ensurePickerElements = useCallback(() => {
    if (overlayRef.current && dottedRef.current && labelRef.current && styleRef.current) {
      return;
    }

    [overlayRef.current, dottedRef.current, labelRef.current, styleRef.current].forEach((el) => {
      if (el) {
        el.remove();
      }
    });
    overlayRef.current = null;
    dottedRef.current = null;
    labelRef.current = null;
    styleRef.current = null;

    const style = document.createElement('style');
    style.textContent = PICKER_STYLE_CSS;

    const overlay = document.createElement('div');
    overlay.className = '__picker-overlay';

    const dotted = document.createElement('div');
    dotted.className = '__picker-dotted';

    const label = document.createElement('div');
    label.className = '__picker-label';

    document.documentElement.append(style, overlay, dotted, label);

    styleRef.current = style;
    overlayRef.current = overlay;
    dottedRef.current = dotted;
    labelRef.current = label;

    (window as any).__elementPicker = { active: activeRef.current, stop: cleanup };
  }, [cleanup]);

  const fmt = (el: Element | null): string => {
  if (!el) {
    return '';
  }
  const tag = el.tagName?.toLowerCase() ?? '';
  const id = el.id ? `#${el.id}` : '';
  const className = getElementClassName(el);
  const cls =
    className && typeof className === 'string'
      ? '.' + className.trim().replace(/\s+/g, '.')
      : '';
  return `${tag}${id}${cls}`;
};

const num = (v: string | number | null | undefined) => Math.max(0, parseFloat(String(v || 0))) || 0;

const moveOverlayFor = useCallback((el: Element | null) => {
  if (!el || !overlayRef.current || !dottedRef.current || !labelRef.current) {
    return;
  }

    const r = el.getBoundingClientRect();

    // Outer overlay (border-box)
    overlayRef.current.style.top = `${Math.max(0, r.top - 2)}px`;
    overlayRef.current.style.left = `${Math.max(0, r.left - 2)}px`;
    overlayRef.current.style.width = `${Math.max(0, r.width)}px`;
    overlayRef.current.style.height = `${Math.max(0, r.height)}px`;

    // Compute paddings & borders for content box
    const cs = getComputedStyle(el);
    const padT = num(cs.paddingTop),
      padR = num(cs.paddingRight);
    const padB = num(cs.paddingBottom),
      padL = num(cs.paddingLeft);
    const borT = num(cs.borderTopWidth),
      borR = num(cs.borderRightWidth);
    const borB = num(cs.borderBottomWidth),
      borL = num(cs.borderLeftWidth);

    const hasPadding = padT + padR + padB + padL > 0;

    if (hasPadding) {
      const x = r.left + borL + padL;
      const y = r.top + borT + padT;
      const w = r.width - (borL + borR + padL + padR);
      const h = r.height - (borT + borB + padT + padB);

      if (w > 0 && h > 0) {
        dottedRef.current.style.display = 'block';
        dottedRef.current.style.left = `${x}px`;
        dottedRef.current.style.top = `${y}px`;
        dottedRef.current.style.width = `${w}px`;
        dottedRef.current.style.height = `${h}px`;
      } else {
        dottedRef.current.style.display = 'none';
      }
    } else {
      dottedRef.current.style.display = 'none';
    }

    // Label
    const tagLabel = el.tagName?.toLowerCase() ?? '';
    const componentName = getNearestComponentName(el);
    const labelText = componentName ? `${componentName}>${tagLabel}` : tagLabel;
    labelRef.current.textContent = labelText;
    const labelRect = labelRef.current.getBoundingClientRect();
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spaceAbove = r.top - margin;
    const spaceBelow = viewportHeight - r.bottom - margin;

    let top: number;
    if (spaceAbove >= labelRect.height || spaceAbove >= spaceBelow) {
      // Prefer positioning above when there is room or more space above.
      top = Math.max(margin, r.top - labelRect.height - margin);
    } else {
      // Otherwise, place the label below while keeping it within the viewport.
      top = Math.min(viewportHeight - labelRect.height - margin, r.bottom + margin);
    }

    const desiredLeft = r.left + r.width / 2 - labelRect.width / 2;
    const left = Math.min(
      Math.max(margin, desiredLeft),
      Math.max(margin, viewportWidth - labelRect.width - margin),
    );

    labelRef.current.style.top = `${top}px`;
    labelRef.current.style.left = `${left}px`;
    labelRef.current.style.transform = '';
  }, []);

  const resolveSvgElement = (el: Element): Element => {
    if (el.namespaceURI === 'http://www.w3.org/2000/svg' && el.tagName.toLowerCase() !== 'svg') {
      const svgAncestor = el.closest('svg');
      if (svgAncestor) {
        return svgAncestor;
      }
    }
    return el;
  };

  const candidateAt = (x: number, y: number): Element | null => {
    const els = document.elementsFromPoint(x, y);
    for (const el of els) {
      if (el === overlayRef.current || el === dottedRef.current || el === labelRef.current) {
        continue;
      }
      return resolveSvgElement(el);
    }
    return null;
  };

  const startPicker = useCallback(() => {
    if (activeRef.current) {
      console.warn('[Element Picker] Already running. Press Esc to stop.');
      return;
    }

    ensurePickerElements();
    activeRef.current = true;

    (window as any).__elementPicker = { active: true, stop: cleanup };

    // Notify parent that picker started
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'ELEMENT_PICKER_STATUS',
          active: true,
          source: 'element-picker',
        },
        '*',
      );
    }

  }, [cleanup, ensurePickerElements]);

  useEffect(() => {
    // Notify parent that element picker is ready
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'ELEMENT_PICKER_READY',
          source: 'element-picker',
        },
        '*',
      );
    }

    const onMove = (e: MouseEvent) => {
      if (!activeRef.current) {
        return;
      }

      const el = candidateAt(e.clientX, e.clientY);
      if (el && el !== targetRef.current) {
        targetRef.current = el;
        moveOverlayFor(el);
      }
    };

    const onClick = (e: MouseEvent) => {
      if (!activeRef.current) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const el = candidateAt(e.clientX, e.clientY) || targetRef.current;
      if (!el) {
        console.warn('[Element Picker] No element found to pick');
        return;
      }

      const selector = fmt(el);
      const rect = el.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(el);

      // Get React component information
      const componentTree = getComponentTree(el);
      const reactComponent = componentTree.reverse().find(f => f.type === "function" && hasSerializableProps(f.props)) || null;

      const elementInfo: ElementInfo = {
        selector,
        tagName: el.tagName,
        id: el.id || undefined,
        className: getElementClassName(el),
        innerText: (el as HTMLElement).innerText?.substring(0, 100) || undefined,
        attributes: Array.from(el.attributes).reduce((acc: any, attr) => {
          acc[attr.name] = attr.value;
          return acc;
        }, {}),
        rect: {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
        styles: {
          display: computedStyle.display,
          position: computedStyle.position,
          zIndex: computedStyle.zIndex,
          color: computedStyle.color,
          backgroundColor: computedStyle.backgroundColor,
        },
        react: {
          component: reactComponent,
          tree: componentTree
        }
      };

      // Send to parent if in iframe
      if (window.parent !== window) {

        // Create a serializable payload with only the data we need
        const serializablePayload = {
          type: 'ELEMENT_PICKED',
          selector: elementInfo.selector,
          tagName: elementInfo.tagName,
          id: elementInfo.id,
          className: elementInfo.className,
          innerText: elementInfo.innerText,
          attributes: elementInfo.attributes,
          rect: elementInfo.rect,
          styles: elementInfo.styles,
          react: elementInfo.react
            ? {
                component: serializeValue(elementInfo.react.component),
                tree: componentTree.length > 0 ? componentTree : undefined,
              }
            : undefined,
          source: 'element-picker',
        };

        window.parent.postMessage(serializablePayload, '*');
      }

      // Disable element picker after selection
      cleanup();
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeRef.current) {
        cleanup();
      }
    };

    const stop = (e: MouseEvent) => {
      if (!activeRef.current) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    };

    // Message handler for postMessage control
    const handleMessage = (event: MessageEvent) => {
      // Only process element picker related messages
      if (event.data?.type === 'ELEMENT_PICKER_CONTROL') {
        if (event.data.enabled && !activeRef.current) {
          startPicker();
        } else if (!event.data.enabled && activeRef.current) {
          cleanup();
        }
      } else if (event.data?.type === 'ELEMENT_PICKER_HIGHLIGHT') {
        const selector = typeof event.data.selector === 'string' ? event.data.selector : null;
        if (!selector) {
          if (!activeRef.current) {
            cleanup();
          }
          return;
        }

        const match = document.querySelector(selector);
        if (!match) {
          if (!activeRef.current) {
            cleanup();
          }
          return;
        }

        ensurePickerElements();
        const resolved = resolveSvgElement(match);
        targetRef.current = resolved;
        moveOverlayFor(resolved);
      }
    };

    // Set up event listeners
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('mousedown', stop, true);
    window.addEventListener('mouseup', stop, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('mousedown', stop, true);
      window.removeEventListener('mouseup', stop, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('message', handleMessage);
      cleanup();
    };
  }, [startPicker, cleanup, moveOverlayFor, ensurePickerElements]);

  return <>{children}</>;
}

export default ElementPickerProvider;
